import { UIRuntimeBuilder, WebComponent } from '@cian/mf-registry/browser';

import { createRoot, hydrateRoot, type Root } from 'react-dom/client';

import { Application } from 'shared/components/App';
import { type IApplicationContext } from 'shared/types/applicationContext';
import { RUNTIME_NAME } from 'shared/constants/microfrontend';
import { type TAdBannerMicrofrontendParameters } from 'shared/types/microfrontendParameters';
import { type TBannerEvents } from 'shared/types/events';

export const createMicrofrontendUI = (context: IApplicationContext) => (): UIRuntimeBuilder => {
  class AdBannerMicrofrontend extends WebComponent<TAdBannerMicrofrontendParameters> {
    protected microfrontendName = context.config.getStrict<string>('projectName');
    protected imageVersion = context.config.getStrict<string>('version.commit');
    protected runtimeName = RUNTIME_NAME;

    #root: Root | null = null;

    public disconnectedCallback() {
      super.disconnectedCallback();

      if (this.#root) {
        this.#root.unmount();
        this.#root = null;
      }
    }

    public async render() {
      const reactElement = (
        <Application
          context={context}
          parameters={this.props}
          instanceId={this.instance.id}
          dispatchLifecycleEvent={this.#dispatchLifecycleEvent}
        />
      );

      if (!this.#root) {
        if (this.shouldHydrate) {
          this.#root = hydrateRoot(this, reactElement);
        } else {
          this.#root = createRoot(this);
          this.#root.render(reactElement);
        }
      } else {
        this.#root.render(reactElement);
      }
    }

    #dispatchLifecycleEvent = (event: TBannerEvents): void => {
      context.eventQueue.emit(event.name, event.payload);
    };
  }

  return { customElementConstructor: AdBannerMicrofrontend };
};
